import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { LoginState } from './typings'
import { StoreData } from '../reducers/typings'
import {
  getBranchesInfo,
  getSalesTeamPositionsData,
  impersonateLogin,
  loginUser,
  refreshToken,
  remoteLogin,
} from './api'
import { getLicenseInfo } from '../licensing/api'
import { clearCookie, getCookie, setCookie } from '@sales-center/common'

const authenticationAdapter = createEntityAdapter<LoginState>({
  selectId: (state) => state.user.userId,
  sortComparer: (a, b) => a.user.userName.localeCompare(b.user.userName),
})

export const initialState = authenticationAdapter.getInitialState<LoginState>({
  authenticated: false,
  remoteLoginAttempted: { sso: false, external: false },
  remoteLoginPending: false,
  user: {
    userName: '',
    companyName: '',
    emailAddress: '',
    token: '',
    impersonated: false,
    externalApplications: [],
    tenantId: '',
    firstName: '',
    clientUrl: '',
    externalProduct: '',
    isAdmin: false,
    userId: '',
    permissions: [],
    branches: [],
    roleId: 0,
    licenseInfo: [],
    tenantLocale: '',
    userDateFormat: '',
    agreementAutoCloseSetting: false,
    isSSOUser: false,
  },
})

export const userState = createSlice({
  name: 'userState',
  initialState,
  reducers: {
    logOut: () => {},
    setUserAuthenticated: (state, { payload }: PayloadAction<boolean>) => {
      state.authenticated = payload
    },
    setRedirectUrl: (state, { payload }: PayloadAction<string>) => {
      state.redirectUrl = payload
    },
    clearRedirectUrl: (state) => {
      state.redirectUrl = undefined
    },
    setAgreementAutoCloseSettingStatus: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.user.agreementAutoCloseSetting = payload
    },
    updateUserPermission: (state, { payload }: PayloadAction<string[]>) => {
      state.user.permissions = payload
    },
    markRemoteLoginAttempted: (
      state,
      { payload }: PayloadAction<'sso' | 'external'>,
    ) => {
      state.remoteLoginAttempted[payload] = true
    },
    resetRemoteLoginAttempted: (
      state,
      { payload }: PayloadAction<('sso' | 'external') | undefined>,
    ) => {
      if (payload) state.remoteLoginAttempted[payload] = false
      else state.remoteLoginAttempted = { sso: false, external: false }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(refreshToken.fulfilled, (state, { payload }) => {
      const impersonated = getCookie('impersonated')
      state.authenticated = true
      state.user = {
        ...payload,
        impersonated: impersonated === 'true',
        branches: undefined,
      }
    })
    builder.addCase(loginUser.fulfilled, (state, { payload }) => {
      clearCookie('impersonated')
      state.authenticated = true
      state.user = {
        ...payload,
        branches: undefined,
      }
    })
    builder.addCase(impersonateLogin.fulfilled, (state, { payload }) => {
      setCookie('impersonated', 'true')
      state.authenticated = true
      state.user = {
        ...payload,
        branches: undefined,
      }
    })
    builder.addCase(remoteLogin.fulfilled, (state, { payload }) => {
      const { redirectUrl, ...user } = payload
      state.authenticated = true
      state.user = {
        ...user,
        branches: undefined,
      }
      state.remoteLoginPending = false
    })
    builder.addCase(getBranchesInfo.fulfilled, (state, { payload }) => {
      state.user.branches = payload
    })
    builder.addCase(
      getSalesTeamPositionsData.fulfilled,
      (state, { payload }) => {
        state.user.salesTeamPositionsData = payload
      },
    )

    builder.addCase(getLicenseInfo.fulfilled, (state, { payload }) => {
      state.user.licenseInfo = payload
    })

    builder.addCase(remoteLogin.pending, (state) => {
      state.remoteLoginPending = true
    })

    builder.addCase(remoteLogin.rejected, (state) => {
      state.remoteLoginPending = false
      // leave attempted as-is; hook will reset it to allow retry
    })
  },
})

export default userState.reducer

export const {
  logOut,
  setUserAuthenticated,
  clearRedirectUrl,
  setRedirectUrl,
  setAgreementAutoCloseSettingStatus,
  updateUserPermission,
  markRemoteLoginAttempted,
  resetRemoteLoginAttempted,
} = userState.actions

export const selectCurrentUser = (state: StoreData) => state.userState.user

export const selectUserAuthenticated = (state: StoreData) =>
  state.userState.authenticated

export const selectUserId = (state: StoreData) => state.userState.user.userId

export const selectUserPermissions = (state: StoreData) => {
  const {
    userState: {
      user: { permissions, isAdmin },
    },
  } = state

  return {
    isAdmin,
    permissions,
  }
}

export const selectRedirectUrl = (state: StoreData) =>
  state.userState.redirectUrl

export const selectRemoteLoginAttempted =
  (mode: 'sso' | 'external') => (state: StoreData) =>
    state.userState.remoteLoginAttempted[mode]

export const selectRemoteLoginPending = (state: StoreData) =>
  state.userState.remoteLoginPending
